// userListDerivation.js
//
// Pure, side-effect-free helpers that derive the User_List view model from the
// managed-user payloads returned by the Admin_User_Api. Follows the same
// conventions as `projectListDerivation.js`: every helper is total (never
// throws, never mutates its input) so one malformed record cannot break the
// admin list render.
//
// The role vocabulary and its ranking come from `services/roles.js` — this
// module only projects them onto list rows and filters. It re-exports the role
// constants so existing importers keep working.
//
// ManagedUser (raw input shape from GET /api/administration/users):
//   {
//     id, public_id, email, first_name, last_name,
//     company, company_title, profile_url,
//     is_admin, is_super_admin, role, role_rank, role_label,
//     created_at, updated_at
//   }

import {
  ROLE_SUPERADMIN,
  ROLE_ADMIN,
  ROLE_EMPLOYEE,
  ROLES,
  ROLE_LABELS,
  rankOf,
  hasAtLeast,
  resolveRole,
  labelForRole
} from './roles'

export {
  ROLE_SUPERADMIN,
  ROLE_ADMIN,
  ROLE_EMPLOYEE,
  ROLES,
  ROLE_LABELS,
  rankOf,
  hasAtLeast,
  labelForRole
}

/**
 * Display placeholder shown in place of any absent row value.
 * @type {string}
 */
export const PLACEHOLDER = '—'

/**
 * Sentinel meaning "do not filter". A filter set to this value (or left
 * empty/absent) is inactive.
 * @type {string}
 */
export const ALL = 'all'

/**
 * Fixed page size for the User_List pagination.
 * @type {number}
 */
export const PAGE_SIZE = 25

/**
 * Coerce an arbitrary value into a trimmed, non-empty display string, or return
 * null when it cannot be sensibly represented as displayable text.
 *
 * @param {*} value
 * @returns {string|null}
 */
function toDisplayString (value) {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed === '' ? null : trimmed
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return null
}

/**
 * Derive whether a raw user record carries administrator access.
 *
 * True for BOTH admin and superadmin — "at least admin" semantics, so the higher
 * tier is never treated as lacking administrator access. Requires a strict role
 * match or an exact `true` boolean, so a truthy-but-wrong value (e.g. the string
 * "false") is treated as an employee rather than silently granted access.
 *
 * @param {*} user
 * @returns {boolean}
 */
export function isAdminUser (user) {
  if (!user || typeof user !== 'object') return false
  return hasAtLeast(resolveRole(user), ROLE_ADMIN)
}

/**
 * Derive whether a raw user record is a super administrator (exact top rank).
 *
 * @param {*} user
 * @returns {boolean}
 */
export function isSuperAdminUser (user) {
  if (!user || typeof user !== 'object') return false
  return resolveRole(user) === ROLE_SUPERADMIN
}

/**
 * Count how many users in a collection carry administrator access (admin or
 * superadmin).
 *
 * The User_List uses this to disable the demote/delete controls on the last
 * account holding administrator access, matching the server's refusal.
 *
 * @param {*} users
 * @returns {number}
 */
export function countAdmins (users) {
  if (!Array.isArray(users)) return 0
  return users.reduce((count, user) => (isAdminUser(user) ? count + 1 : count), 0)
}

/**
 * Count how many users in a collection are super administrators.
 *
 * Used to disable the demote/delete controls on the last super administrator.
 *
 * @param {*} users
 * @returns {number}
 */
export function countSuperAdmins (users) {
  if (!Array.isArray(users)) return 0
  return users.reduce((count, user) => (isSuperAdminUser(user) ? count + 1 : count), 0)
}

/**
 * Assemble a display name from the first/last name fields, falling back to the
 * email and then to the placeholder when no name is stored.
 *
 * @param {*} user
 * @returns {string}
 */
export function deriveDisplayName (user) {
  const source = user && typeof user === 'object' ? user : {}
  const first = toDisplayString(source.first_name)
  const last = toDisplayString(source.last_name)
  const name = [first, last].filter(Boolean).join(' ')
  if (name !== '') return name
  return toDisplayString(source.email) || PLACEHOLDER
}

/**
 * Derive a single User_List row view model from a raw managed-user payload.
 *
 * `currentPublicId` is the signed-in administrator's own `public_id`; the
 * matching row is flagged `isSelf` so the view can mark it and disable the
 * actions the server refuses on your own account. Comparison is by `public_id`
 * because that is the identifier the login response persists into the session
 * profile.
 *
 * @param {*} user
 * @param {string|null} [currentPublicId]
 * @returns {object} the row view model
 */
export function deriveUserRow (user, currentPublicId) {
  const source = user && typeof user === 'object' ? user : {}
  const role = resolveRole(source)
  const publicId = toDisplayString(source.public_id)

  return {
    id: source.id != null ? source.id : null,
    publicId,
    email: toDisplayString(source.email) || PLACEHOLDER,
    displayName: deriveDisplayName(source),
    company: toDisplayString(source.company) || PLACEHOLDER,
    companyTitle: toDisplayString(source.company_title) || PLACEHOLDER,
    role,
    roleRank: rankOf(role),
    roleLabel: labelForRole(role),
    isAdmin: hasAtLeast(role, ROLE_ADMIN),
    isSuperAdmin: role === ROLE_SUPERADMIN,
    // Only ever true for a real, non-empty id match, so a null/absent
    // public_id on either side never accidentally marks a row as "you".
    isSelf: Boolean(publicId && currentPublicId && publicId === currentPublicId)
  }
}

/**
 * Derive the full set of User_List rows.
 *
 * @param {*} users
 * @param {string|null} [currentPublicId]
 * @returns {object[]}
 */
export function deriveUserRows (users, currentPublicId) {
  if (!Array.isArray(users)) return []
  return users.map((user) => deriveUserRow(user, currentPublicId))
}

/**
 * Filter derived rows by a case-insensitive name/email search and a role.
 *
 * An absent, empty, or `ALL` criterion is inactive. Both criteria are ANDed, so
 * a row must satisfy every active one to be kept. The role filter matches the
 * role EXACTLY, so filtering by "Administrator" does not include super
 * administrators — in a filter, precision is more useful than rank semantics.
 *
 * @param {object[]} rows
 * @param {{search?: string, role?: string}} [criteria]
 * @returns {object[]}
 */
export function filterUserRows (rows, criteria) {
  if (!Array.isArray(rows)) return []

  const input = criteria && typeof criteria === 'object' ? criteria : {}
  const search = typeof input.search === 'string' ? input.search.trim().toLowerCase() : ''
  const role = typeof input.role === 'string' && input.role !== '' ? input.role : ALL

  return rows.filter((row) => {
    if (!row || typeof row !== 'object') return false

    if (search !== '') {
      const haystack = [row.displayName, row.email]
        .filter((v) => typeof v === 'string')
        .join(' ')
        .toLowerCase()
      if (haystack.indexOf(search) === -1) return false
    }

    if (role !== ALL && row.role !== role) return false

    return true
  })
}

/**
 * Chunk rows into pages of at most `size`.
 *
 * Returns an empty array (not `[[]]`) for an empty input so the caller can treat
 * "no pages" and "no rows" identically. A non-positive or invalid size falls
 * back to `PAGE_SIZE` rather than looping forever.
 *
 * @param {object[]} rows
 * @param {number} [size=PAGE_SIZE]
 * @returns {object[][]}
 */
export function paginateUsers (rows, size) {
  if (!Array.isArray(rows) || rows.length === 0) return []

  const pageSize =
    Number.isInteger(size) && size > 0 ? size : PAGE_SIZE

  const pages = []
  for (let i = 0; i < rows.length; i += pageSize) {
    pages.push(rows.slice(i, i + pageSize))
  }
  return pages
}

export default {
  PLACEHOLDER,
  ALL,
  PAGE_SIZE,
  isAdminUser,
  isSuperAdminUser,
  countAdmins,
  countSuperAdmins,
  deriveDisplayName,
  deriveUserRow,
  deriveUserRows,
  filterUserRows,
  paginateUsers
}
